import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { ISizeName } from '@df/core/service/client';
import { Status } from '@df/core/status';
import { DfImageWrapperComponent } from '@df/module-image/df-image-wrapper.component';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { ratioField } from '@df/module-image/icms/icms-ratio-field';
import { ImageModule } from '@df/module-image/image.module';
import { CarouselModule } from '@df/ui-carousel/carousel.module';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { Swiper, SwiperEvents } from 'swiper/types';
import { defaultImagePng } from '../../../icms.config';
import { LoginFormRetailComponent } from '../../../login/form/retail/login-form-retail.component';
import { NMultisiteModal } from '../../../multisite/modal/multisite-modal.interfaces';
import { MultisiteSimpleLabelComponent } from '../../../multisite/simple-label/multisite-simple-label.component';
import { IconsOptions } from '../../../styles/cms.config';
import { ActionComponent } from '../../../ui/api/action.component';
import { ResultComponent } from '../../../ui/resut/result.component';
import { FirebasePermissionService } from '../firebase/firebase-permission.service';

export namespace NPwaTutorialModal {
    export const MODAL_ID = 'pwa-tutorial-screen-modal';
    export const FLAG_NAME = '_oneTime' + MODAL_ID;

    export type IBlockData = Catalog.IBlockData<IData>;

    export interface IData {
        notOpenTutorialModal: boolean;
        mainImage: string;
        mainLogo: string;
        mainLogoRatio: string;
        mainOverlayOpacity: number;
        mainDarkMode: boolean;
        mainScreenContentPosition: number;
        welcomeTitle: string;
        welcomeDescription: string;
        welcomeButtonCopy: string;
        tutorialScreenBgImage: string;
        notificationTitle: string;
        notificationDescription: string;
        notificationList: IIconList[];
        notificationButtonCopy: string;
        notificationSkipButtonCopy: string;
        simpleSteps: ISimpleStep[];
        signInButtonCopy: string;
        guestButtonCopy: string;
        continueButtonCopy: string;
        skipButtonCopy: string;
        accountScreenLogoPosition: number;
        loginImage?: string;
        loginImageRatio?: string;
    }

    export interface IIconList {
        icon?: string;
        text: string;
    }

    export interface ISimpleStep {
        title: string;
        description: string;
        list: IIconList[];
    }

    export enum ESlideType {
        notification = 'notification',
        simpleStep = 'simpleSteps'
    }

    export interface ISlide {
        type: ESlideType;
        data?: ISimpleStep;
    }

    export enum IStepType {
        welcome = 'welcome',
        slides = 'slides',
        login = 'login'
    }

    export const iconListFields = [
        <NICmsContentField.IChoiceField>{
            key: 'icon',
            title: 'Icon',
            type: NICmsContentField.EFieldType.choice,
            required: true,
            options: IconsOptions,
            previewMode: 'icon',
            default: 'icon-star'
        },
        <NICmsContentField.IField>{
            key: 'text',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Text',
            default: 'Lorem ipsum dolor',
            required: true
        }
    ];
}

@DfCmsModal({
    id: NPwaTutorialModal.MODAL_ID,
    position: 'custom',
    name: 'PWA Tutorial Screen',
    config: (sizeId: ISizeName) => ({
        width: '100vw',
        height: '100vh',
        minWidth: '100%',
        minHeight: '100%',
        position: {
            top: '0',
            right: '0',
            bottom: '0'
        },
        modalClass: 'p-a-0 bg-col-51'
    }),
    blockGroup: 'modals',
    priority: 100,
    reopenPolicy: NDfModal.ReopenPolicy.first,
    sizeRestriction: sizeId => ['S', 'M', 'L', 'X']
})
@ICmsComponent<PwaTutorialModalComponent>({
    id: NPwaTutorialModal.MODAL_ID,
    block: {
        url: NPwaTutorialModal.MODAL_ID
    },
    onDemandOnly: true,
    name: 'PWA Tutorial Screen',
    fields: [
        <NICmsContentField.IToggleField>{
            key: 'notOpenTutorialModal',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Not open tutorial modal',
            default: false
        },
        <NICmsContentField.IField>{
            key: 'mainImage',
            type: NICmsContentField.EFieldType.image,
            title: 'Main image',
            default: defaultImagePng,
            required: true
        },
        <NICmsContentField.IField>{
            key: 'mainLogo',
            type: NICmsContentField.EFieldType.image,
            title: 'Main logo',
            default: defaultImagePng,
            required: true
        },
        ratioField({
            key: 'mainLogoRatio',
            title: 'Main logo ratio',
            default: '250/55',
            required: true
        }),
        <NICmsContentField.ISliderField>{
            key: 'mainOverlayOpacity',
            type: NICmsContentField.EFieldType.slider,
            title: 'Main overlay opacity',
            default: 0.8,
            required: true,
            min: 0,
            max: 1,
            step: 0.05
        },
        <NICmsContentField.IToggleField>{
            key: 'mainDarkMode',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Main dark mode',
            default: false,
            required: true
        },
        <NICmsContentField.IField>{
            key: 'continueButtonCopy',
            type: NICmsContentField.EFieldType.text,
            title: 'Continue button copy',
            default: 'Continue',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'skipButtonCopy',
            type: NICmsContentField.EFieldType.text,
            title: 'Skip button copy',
            default: 'Skip',
            required: true
        },
        <NICmsContentField.ISliderField>{
            key: 'mainScreenContentPosition',
            type: NICmsContentField.EFieldType.slider,
            title: 'Main screen content position',
            default: 50,
            required: true,
            min: 0,
            max: 100,
            step: 1
        },
        <NICmsContentField.IField>{
            key: 'welcomeTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Welcome title',
            default: 'Welcome to the LeMieux App',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'welcomeDescription',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Welcome description',
            default:
                'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla ultricies odio accumsan pulvinar aliquet. Duis vel odio est. Etiam vitae faucibus ex. Aliquam erat volutpat.',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'welcomeButtonCopy',
            type: NICmsContentField.EFieldType.text,
            title: 'Welcome button copy',
            default: 'Get started',
            required: true
        },
        {
            key: 'tutorialLabel',
            type: NICmsContentField.EFieldType.label,
            title: 'Tutorial section'
        },
        {
            key: 'tutorialScreenBgImage',
            type: NICmsContentField.EFieldType.image,
            title: 'Tutorial screen background image',
            default: defaultImagePng,
            required: true
        },
        <NICmsContentField.IField>{
            key: 'notificationTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Notification title',
            default: 'Don’t miss a thing',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'notificationDescription',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Notification description',
            default: 'Turn on notifications to stay up to date, duis vel odio est. Etiam vitae faucibus ex.',
            required: true
        },
        <NICmsContentField.IRepeaterField>{
            key: 'notificationList',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Notification list',
            required: true,
            itemName: 'Notification',
            itemNameField: 'text',
            defaultSize: 4,
            fields: NPwaTutorialModal.iconListFields
        },
        <NICmsContentField.IField>{
            key: 'notificationButtonCopy',
            type: NICmsContentField.EFieldType.text,
            title: 'Notification button copy',
            default: 'Allow notifications',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'notificationSkipButtonCopy',
            type: NICmsContentField.EFieldType.text,
            title: 'Notification skip button copy',
            default: 'Not now',
            required: true
        },
        <NICmsContentField.IRepeaterField>{
            key: 'simpleSteps',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Simple steps',
            itemName: 'Step',
            itemNameField: 'title',
            complex: true,
            fields: [
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'title',
                    title: 'Step title',
                    required: true
                },
                {
                    key: 'description',
                    type: NICmsContentField.EFieldType.textarea,
                    title: 'Step description',
                    required: true
                },
                {
                    key: 'list',
                    type: NICmsContentField.EFieldType.repeater,
                    title: 'List',
                    required: true,
                    itemName: 'Item',
                    itemNameField: 'text',
                    fields: NPwaTutorialModal.iconListFields
                }
            ],
            default: [
                {
                    title: 'Our Community',
                    description: 'Join the fastest-growing community of LeMieux fans online and collect real-world rewards!',
                    list: []
                },
                {
                    title: 'Our Rewards Scheme',
                    description: 'LeMieux Insiders rewards effort - just like horses do.',
                    list: []
                }
            ]
        },
        {
            key: 'accountScreenLabel',
            type: NICmsContentField.EFieldType.label,
            title: 'Account screen'
        },
        <NICmsContentField.ISliderField>{
            key: 'accountScreenLogoPosition',
            type: NICmsContentField.EFieldType.slider,
            title: 'Account screen logo position',
            default: 50,
            required: true,
            min: 0,
            max: 100,
            step: 1
        },
        {
            key: 'loginImage',
            type: NICmsContentField.EFieldType.image,
            title: 'Login image'
        },
        ratioField({
            key: 'loginImageRatio',
            title: 'Login image ratio',
            default: '16/9'
        })
    ]
})
@Component({
    selector: 'pwa-tutorial-modal',
    templateUrl: './pwa-tutorial-modal.html',
    styleUrls: ['pwa-tutorial-modal.scss'],
    standalone: true,
    imports: [
        ICmsClientModule,
        CommonModule,
        CarouselModule,
        TranslationModule,
        ImageModule,
        LoginFormRetailComponent,
        DfImageDirective,
        MultisiteSimpleLabelComponent,
        ActionComponent,
        ResultComponent,
        DfImageWrapperComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PwaTutorialModalComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NPwaTutorialModal.IData;

    protected currentStep: NPwaTutorialModal.IStepType = NPwaTutorialModal.IStepType.welcome;
    protected readonly EStepType = NPwaTutorialModal.IStepType;
    protected readonly ESlideType = NPwaTutorialModal.ESlideType;
    protected readonly multisiteModalId = NMultisiteModal.componentId;
    protected swiper?: Swiper;
    protected isRegister = false;
    protected isWelcomeContentVisible = false;
    protected isAccountForm = false;
    protected allowNotificationsStatus = new Status();

    constructor(
        private modalRef: ModalRef<NPwaTutorialModal.IData>,
        private localStorageService: LocalStorageService,
        private firebasePermissionService: FirebasePermissionService
    ) {
        super();
    }

    protected close() {
        this.localStorageService.set(NPwaTutorialModal.FLAG_NAME, 1);
        this.modalRef?.close();
    }

    protected onAfterSwiperInit(eventParams: Parameters<SwiperEvents['afterInit']>): void {
        const [swiper] = eventParams;
        this.swiper = swiper;
    }

    protected nextSlide(): void {
        if (this.swiper?.activeIndex === this.slides.length - 1) {
            this.changeStep(NPwaTutorialModal.IStepType.login);
            this.localStorageService.set(NPwaTutorialModal.FLAG_NAME, 1);
        } else {
            this.swiper?.slideNext();
        }
    }

    protected get slides(): NPwaTutorialModal.ISlide[] {
        return this.content?.simpleSteps?.length
            ? [
                  { type: NPwaTutorialModal.ESlideType.notification },
                  ...this.content.simpleSteps.map(step => ({ type: NPwaTutorialModal.ESlideType.simpleStep, data: step }))
              ]
            : [{ type: NPwaTutorialModal.ESlideType.notification }];
    }

    protected openWelcomeContent(): void {
        this.isWelcomeContentVisible = true;
        this.detectChanges();
    }

    protected changeStep(step: NPwaTutorialModal.IStepType) {
        if (step !== this.currentStep) {
            this.currentStep = step;
            this.detectChanges();
        }
    }

    protected openAccountForm(openRegister: boolean = false) {
        this.isAccountForm = true;
        this.isRegister = openRegister;
        this.detectChanges();
    }

    protected async allowNotifications() {
        if (this.client.isCms) {
            this.nextSlide();
            return;
        }
        this.allowNotificationsStatus.busy = true;
        await this.firebasePermissionService.requestPermission();
        this.nextSlide();
        this.allowNotificationsStatus.busy = false;
    }
}
